<template>
  <div class="material-assistant-step2">
    <div class="material-assistant-step2__card">
      <div class="material-assistant-step2__card__content">
        <div class="material-assistant-step2__card__content__card-title">
          <h3 class="h4 material-assistant-step2__card__content__card-title__text">
            {{ i18n.tv('Frontend.Title.Temperature', 'ORingConfiguratorImport') }}
          </h3>
          <span>
            <ActionInfoFilledIcon
              @click="openPopup"
            />
          </span>
          <PopupBase
            v-if="showPopup"
            :show="showPopup"
            @close-pop-up="closePopup"
            :size="popupSizeSmall"
          >
            <PopupContent
              :title="popupContent.title"
              :content="popupContent.content"
            />
          </PopupBase>
        </div>
        <div class="material-assistant-step2__card__content__card-description">
          {{ i18n.tv('Frontend.Message.Temperature', 'ORingConfiguratorImport') }}
        </div>
        <div class="material-assistant-step2__card__content__card-input-container">
          <label
            id="step-temperature-label"
            for="step-temperature"
            class="material-assistant-step2__card__content__card-input-container__temp-label"
          >
            {{ i18n.tv('Frontend.Parameter.TemperatureMin', 'ORingConfiguratorImport') }} [{{ temperatureUnit }}]
          </label>
          <NumericInput
            class="material-assistant-step2__label-input"
            id="step-temperature"
            aria-labelledby="step-temperature-label"
            v-model="minTemperature"
            :spinners="true"
            :step="1"
            :min="metricRanges?.tempMin"
            :max="metricRanges?.tempMax"
            :decimals="temperatureDecimals"
            :format="temperatureFormat"
            new-style-with-spinner
          />
          <div class="input-info">
            <template v-if="metricRanges">
              {{ formatTemperature(metricRanges.tempMin) }} - {{ formatTemperature(metricRanges.tempMax) }}
            </template>
            <template
              v-else
            >
              &nbsp;
            </template>
          </div>
        </div>
        <div class="material-assistant-step2__card__content__card-input-container">
          <label
            id="step-temperature-max-label"
            for="step-temperature-max"
            class="material-assistant-step2__card__content__card-input-container__temp-label"
          >
            {{ i18n.tv('Frontend.Parameter.TemperatureMax', 'ORingConfiguratorImport') }} [{{ temperatureUnit }}]
          </label>
          <NumericInput
            class="material-assistant-step2__label-input"
            v-model="maxTemperature"
            :spinners="true"
            :step="1"
            :min="metricRanges?.tempMin"
            :max="metricRanges?.tempMax"
            :decimals="temperatureDecimals"
            :format="temperatureFormat"
            new-style-with-spinner
            id="step-temperature-max"
            aria-labelledby="step-temperature-max-label"
          />
          <div class="input-info">
            <template v-if="metricRanges">
              {{ formatTemperature(metricRanges.tempMin) }} - {{ formatTemperature(metricRanges.tempMax) }}
            </template>
            <template
              v-else
            >
              &nbsp;
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import NumericInput from '~/components/input-components/NumericInput.vue'
import {useORingCalculatorStore} from '~/store/oring-calculator'
import useI18nExtended from '~/composables/usei18nExtended'
import {useOringCalculatorService} from '@/composables/oring_calc_service'
import ActionInfoFilledIcon from '~/assets/icons/action_info_filled.svg?component'
import PopupContent from '../PopupContent.vue'
import {POPUP} from '~/assets/js/constants'

const i18n = useI18nExtended()
const showPopup = ref(false)
const popupContent = {
  title: i18n.tv('Frontend.Title.Temperature', 'ORingConfiguratorImport'),
  content: i18n.tv('Frontend.Info.TemperatureOne', 'ORingConfiguratorImport')
}
const popupSizeSmall = POPUP.Size.small

const openPopup = () => {
  showPopup.value = true
}
const closePopup = () => {
  showPopup.value = false
}

const oRingCalculatorStore = useORingCalculatorStore()
const {
  materialAssistantMinTemp,
  materialAssistantMaxTemp,
  oringMaterialAssistantMetricRanges,
  oringCalculatorImperial,
  oringCalculatorFahrenheit
} = storeToRefs(oRingCalculatorStore)

const minTemperature = materialAssistantMinTemp
const maxTemperature = materialAssistantMaxTemp
const temperatureUnit = computed(() => {
  return oringCalculatorFahrenheit.value ? '°F' : '°C'
})

const temperatureDecimals = computed(() => oringCalculatorFahrenheit.value ? 2 : 1)
const temperatureFormat = computed(() => oringCalculatorFahrenheit.value ? 'n2' : 'n1')

const metricRanges = computed(() => oringMaterialAssistantMetricRanges.value)

const formatTemperature = (val: number) => {
  if (val === null || val === undefined) {
    return ''
  }
  const decimals = oringCalculatorFahrenheit.value ? 2 : 1
  const unit = oringCalculatorFahrenheit.value ? '°F' : '°C'
  return val.toLocaleString(undefined, {minimumFractionDigits: decimals, maximumFractionDigits: decimals}) + ' ' + unit
}

onMounted(async () => {
  if (!oringMaterialAssistantMetricRanges.value) {
    await fetchMetricRanges()
  }
})

async function fetchMetricRanges() {
  const {getMetricRangesMaterialAssistant} = useOringCalculatorService()
  oringMaterialAssistantMetricRanges.value = await getMetricRangesMaterialAssistant({
    language: i18n.locale.value,
    imperial: oringCalculatorImperial.value,
    fahrenheit: oringCalculatorFahrenheit.value
  })
}

const validate = () => {
  const metricRanges = oringMaterialAssistantMetricRanges.value
  return (
    metricRanges &&
    minTemperature.value !== null &&
    maxTemperature.value !== null &&
    minTemperature.value >= metricRanges.tempMin &&
    minTemperature.value <= metricRanges.tempMax &&
    maxTemperature.value >= metricRanges.tempMin &&
    maxTemperature.value <= metricRanges.tempMax
  )
}
defineExpose({validate})

watch([oringCalculatorFahrenheit], async ([newFahrenheit], [oldFahrenheit]) => {
  if (newFahrenheit !== oldFahrenheit) {
    await fetchMetricRanges()
  }
})
</script>

<style lang="scss">
.material-assistant-step2 {
  &__card {
    padding: 1rem;
    box-shadow: $shadow-default;
    margin: 1.25rem 0;

    &__content {
      &__card-title {
        display: flex;
        align-items: center;
        gap: .5rem;
        margin: 0;
        flex-direction: row;

        h3 {
          margin: 0;
        }

        &__text {
          margin: 0;
          font-weight: $base-text-font-weight-semi-bold;
          font-size: 1.125rem;
        }

        span {
          display: flex;
          margin-bottom: 1rem;
        }

        .icon.icon--svg {
          height: 1.5rem;
          width: 1.5rem;
          fill: $color__fst__primary;
          cursor: pointer;
        }
      }

      &__card-description {
        font-weight: $base-text-font-weight-light;
      }

      &__card-input-container {
        display: flex;
        flex-direction: column;
        margin-top: 1rem;
        max-width: 11.6875rem;

       label {
        margin-bottom: .375rem;
        font-weight: $base-text-font-weight-light;
       }

       .input-info {
         font-size: 0.75rem;
         font-weight: $base-text-font-weight-light;
         padding-top: .25rem;
       }
      }
    }
  }
}
</style>
